/**
 * Drag-&-Drop einer Datei-Zeile auf einen Ordner. Das Ziel entscheidet die Achse (`decideDrop`):
 * selbe Top-Kategorie = Move, andere Kategorie = Reclassify (still vs. Dialog entscheidet der
 * Aufrufer); der Sidebar-Papierkorb ist ein kategorieunabhängiges Drop-Ziel (Soft-Delete + Undo).
 * Ungültige Ziele highlighten nicht. Verweilen ~500 ms über einem Ordner = Spring-Loading
 * (`useSpringLoad`). Gesperrte Belege sind nicht ziehbar; die Menüs bleiben der a11y-Weg.
 *
 * Gehört die gezogene Datei zu einer **Mehrfach-Auswahl** (> 1), ist die ganze Auswahl das Payload
 * (`bulk`): der Drop wendet die Bulk-Aktion auf alle an. Sonst der Einzel-Pfad (still + Undo).
 */
import { useState } from 'react';
import type { PropertyFileResponse } from '@/shared/api';
import { decideBatchDrop, decideDrop } from './akteReclassifyRules';
import { useSpringLoad } from './useSpringLoad';

/** Bulk-Verhalten, wenn ein Mehrfach-Payload (Auswahl > 1) gezogen wird. */
export interface DragBulkConfig {
  /** Aktuelle Auswahl (Datei-Objekte); bestimmt, ob ein Drag zum Batch wird. */
  selectedFiles: readonly PropertyFileResponse[];
  onMove: (files: PropertyFileResponse[], targetFolder: string) => void;
  onTrash: (files: PropertyFileResponse[]) => void;
  onReclassify: (files: PropertyFileResponse[], targetFolder: string) => void;
}

export function useAkteDragMove(
  onMoveTo?: (file: PropertyFileResponse, targetFolder: string) => void,
  onTrash?: (file: PropertyFileResponse) => void,
  /** Cross-Kategorie-Drop: der Aufrufer entscheidet still (Undo-Toast) vs. Reclassify-Dialog. */
  onReclassify?: (file: PropertyFileResponse, targetFolder: string) => void,
  bulk?: DragBulkConfig,
) {
  const [dragFile, setDragFile] = useState<PropertyFileResponse | null>(null);
  // Gesetzt (Länge > 1), wenn die gezogene Datei zu einer Mehrfach-Auswahl gehört → Bulk-Payload.
  const [dragBatch, setDragBatch] = useState<PropertyFileResponse[] | null>(null);
  const [overPath, setOverPath] = useState<string | null>(null);
  const [overTrash, setOverTrash] = useState(false);
  const { scheduleSpring, cancelSpring } = useSpringLoad();

  function reset() {
    cancelSpring();
    setDragFile(null);
    setDragBatch(null);
    setOverPath(null);
    setOverTrash(false);
  }

  function dragProps(file: PropertyFileResponse) {
    if (file.locked || (!onMoveTo && !onTrash && !onReclassify && !bulk)) return {};
    return {
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        // Manche Browser starten den Drag nur, wenn dataTransfer gesetzt ist.
        e.dataTransfer.setData('text/plain', file.id);
        e.dataTransfer.effectAllowed = 'move';
        setDragFile(file);
        // Ist die gezogene Datei Teil einer Mehrfach-Auswahl, wird die ganze Auswahl zum Payload.
        const selected = bulk?.selectedFiles ?? [];
        setDragBatch(
          selected.length > 1 && selected.some((f) => f.id === file.id) ? [...selected] : null,
        );
      },
      onDragEnd: reset,
    };
  }

  /** Akzeptiert der Ordner den Drop des aktuellen Payloads (Batch oder Einzeldatei)? */
  function canDrop(targetFolder: string): boolean {
    if (dragBatch && bulk) return decideBatchDrop(dragBatch, targetFolder) !== 'none';
    if (!dragFile) return false;
    const d = decideDrop(dragFile.folder, targetFolder);
    if (d.action === 'move') return !!onMoveTo;
    if (d.action === 'silent' || d.action === 'dialog') return !!onReclassify;
    return false;
  }

  /** Führt den Drop aus (Batch-Bulk-Op oder Einzel-Achse). */
  function dispatchDrop(targetFolder: string) {
    if (dragBatch && bulk) {
      const action = decideBatchDrop(dragBatch, targetFolder);
      if (action === 'move') bulk.onMove(dragBatch, targetFolder);
      else if (action === 'reclassify') bulk.onReclassify(dragBatch, targetFolder);
      return;
    }
    if (!dragFile) return;
    const d = decideDrop(dragFile.folder, targetFolder);
    if (d.action === 'move' && onMoveTo) onMoveTo(dragFile, targetFolder);
    else if ((d.action === 'silent' || d.action === 'dialog') && onReclassify) {
      onReclassify(dragFile, targetFolder);
    }
  }

  /**
   * Drop-Handler + Aktiv-Flag für einen Ordner. `onSpring` (optional) wird nach der Verweildauer
   * aufgerufen, wenn der Zeiger beim Ziehen über dem Ordner bleibt (Sidebar: aufklappen, Content:
   * hineinnavigieren) — auch für Ordner, die selbst kein gültiges Drop-Ziel sind.
   */
  function dropProps(targetFolder: string, onSpring?: () => void) {
    if (!onMoveTo && !onReclassify && !bulk) return { props: {}, active: false };
    return {
      active: overPath === targetFolder && canDrop(targetFolder),
      props: {
        onDragOver: (e: React.DragEvent) => {
          if (!dragFile) return;
          if (onSpring) scheduleSpring(targetFolder, onSpring);
          if (!canDrop(targetFolder)) return;
          e.preventDefault();
          setOverPath(targetFolder);
        },
        onDragLeave: (e: React.DragEvent) => {
          // dragleave feuert auch beim Wechsel auf Kind-Elemente — echtes Verlassen nur, wenn das
          // Ziel den neuen Zeiger-Knoten nicht mehr enthält (sonst flackern Highlight/Spring-Timer).
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          cancelSpring();
          setOverPath((p) => (p === targetFolder ? null : p));
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          dispatchDrop(targetFolder);
          reset();
        },
      },
    };
  }

  /** Drop-Handler + Aktiv-Flag für den Papierkorb: jede gezogene (nicht gesperrte) Datei/Auswahl ist
   *  ein gültiges Ziel → Soft-Delete (Einzel: still + Undo · Batch: Bulk-Delete via Confirm). */
  function trashDropProps() {
    if (!onTrash && !bulk) return { props: {}, active: false };
    return {
      active: overTrash && !!dragFile,
      props: {
        onDragOver: (e: React.DragEvent) => {
          if (!dragFile) return;
          e.preventDefault();
          setOverTrash(true);
        },
        onDragLeave: () => setOverTrash(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          if (dragBatch && bulk) bulk.onTrash(dragBatch);
          else if (dragFile && onTrash) onTrash(dragFile);
          reset();
        },
      },
    };
  }

  return { dragProps, dropProps, trashDropProps };
}
